import type { Action } from 'svelte/action';

const SELECTED = '[aria-selected="true"], [aria-checked="true"], [aria-current="true"], [aria-current="page"]';

/**
 * The raised pill of a `.tabs` track travels to the selected `.tab` (ADR-060). It only writes the
 * pill's place into CSS variables, so the tabs keep their own roles, keys and clicks. Only a change
 * of selection is a move. Its first place, a webfont arriving or a count filling in a tab just put
 * it where it belongs, or the pill would slide on its own (employer, 2026-11-01). With nothing
 * selected the pill hides.
 */
export const tabIndicator: Action<HTMLElement> = (track) => {
  let frame = 0;
  const place = () => {
    const tab = track.querySelector<HTMLElement>(SELECTED);
    track.style.setProperty('--tab-on', tab ? '1' : '0');
    if (!tab) return;
    track.style.setProperty('--tab-x', `${tab.offsetLeft}px`);
    track.style.setProperty('--tab-w', `${tab.offsetWidth}px`);
  };
  /** Places it without travelling, and lets it travel again once that place has been drawn. */
  const settle = () => {
    track.dataset.pill = '';
    place();
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => (track.dataset.pill = 'moving'));
    });
  };
  settle();
  const sizes = typeof ResizeObserver === 'function' ? new ResizeObserver(settle) : null;
  sizes?.observe(track);
  const changes = new MutationObserver((records) => {
    if (records.some((r) => r.type === 'attributes')) place();
    else settle();
  });
  changes.observe(track, { subtree: true, childList: true, attributes: true, attributeFilter: ['aria-selected', 'aria-checked', 'aria-current'] });
  return {
    destroy() {
      cancelAnimationFrame(frame);
      sizes?.disconnect();
      changes.disconnect();
    },
  };
};
